import { track } from "@/lib/analytics"
import { uploadRefusalMessage } from "@/lib/quotaGate"
import { forwardRef, useImperativeHandle, useRef, useState, type DragEvent } from "react"
import { uploadMatterFiles } from "../api"
import { ACCEPT } from "../landing/Composer"
import "./WorkspaceDrop.css"

/** What a reader can drop in, named the way they know the papers. */
export const DROP_EXAMPLES = [
  "Show-cause notice (SCN / DRC-01)",
  "Your reply",
  "The order (OIO)",
  "Invoices",
  "GSTR-1, 2B, 3B extracts",
  "Contracts or agreements",
  "Correspondence",
]

/** The formats the uploader takes (see ACCEPT). */
export const DROP_FORMATS = "PDF, Word (.docx), Excel (.xlsx), phone photos (JPG, PNG) and plain text. Add as many as you like at once."

type Status = "queued" | "uploading" | "done" | "failed"
interface Item { id: number; file: File; status: Status; error?: string }

export const sizeOf = (bytes: number | undefined): string => {
  if (!bytes || bytes < 0) return ""
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const WORDS: Record<Status, string> = { queued: "Waiting", uploading: "Uploading…", done: "Added", failed: "Not added" }
// The upload request reports no byte progress, so "uploading" is an honest moving bar, not a made-up percentage.
const FILL: Record<Status, number> = { queued: 0, uploading: 1, done: 1, failed: 1 }

/**
 * Adds files to a workspace, one request per file so each shows its own progress and status.
 * `hero` is the empty workspace's whole point; `compact` is the quiet "Add files" control once files exist.
 */
export interface DropHandle { take: (files: File[], via: "drop" | "picker") => void }

export const WorkspaceDrop = forwardRef<DropHandle, { caseId: string; variant: "hero" | "compact"; onChanged: () => void }>(function WorkspaceDrop({ caseId, variant, onChanged }, handle) {
  const [over, setOver] = useState(false)
  const [items, setItems] = useState<Item[]>([])
  const input = useRef<HTMLInputElement>(null)
  const running = useRef(false)
  const next = useRef(1)
  const pending = items.some((i) => i.status === "queued" || i.status === "uploading")

  const patch = (id: number, to: Partial<Item>) => setItems((cur) => cur.map((i) => (i.id === id ? { ...i, ...to } : i)))

  const run = async (batch: Item[], via: "drop" | "picker") => {
    if (running.current) return
    running.current = true
    let added = 0
    for (const it of batch) {
      patch(it.id, { status: "uploading", error: undefined })
      try {
        await uploadMatterFiles(caseId, [it.file])
        patch(it.id, { status: "done" })
        added++
      } catch (err) {
        patch(it.id, { status: "failed", error: uploadRefusalMessage(err) ?? "Could not be added." })
      }
    }
    running.current = false
    if (added) {
      track("matter_file_added", { count: added, via })
      onChanged()
      // The shelf now lists what was added; the quiet control forgets it. The hero keeps its list until the page moves on.
      if (variant === "compact") setItems((cur) => cur.filter((i) => i.status === "failed"))
    }
  }

  const take = (picked: File[], via: "drop" | "picker") => {
    if (!picked.length || running.current) return
    const batch = picked.map((file) => ({ id: next.current++, file, status: "queued" as Status }))
    setItems((cur) => [...cur.filter((i) => i.status !== "done"), ...batch])
    void run(batch, via)
  }

  useImperativeHandle(handle, () => ({ take }))

  const onDrop = (e: DragEvent) => {
    e.preventDefault()
    setOver(false)
    take(Array.from(e.dataTransfer?.files ?? []), "drop")
  }

  const retry = (it: Item) => void run([it], "picker")

  const status = items.length ? (
    <ul className="wsd-items" aria-label="Files being added">
      {items.map((i) => (
        <li key={i.id} className={`wsd-item ${i.status}`}>
          <span className="wsd-name" title={i.file.name}>{i.file.name}</span>
          <span className="wsd-size">{sizeOf(i.file.size)}</span>
          <span className="wsd-state" role={i.status === "failed" ? "alert" : undefined}>
            {i.status === "failed" ? i.error : WORDS[i.status]}
            {i.status === "failed" ? <button type="button" className="wsd-retry" onClick={() => retry(i)}>Try again</button> : null}
          </span>
          <span className="wsd-bar" aria-hidden="true"><span style={{ transform: `scaleX(${FILL[i.status]})` }} /></span>
        </li>
      ))}
    </ul>
  ) : null

  const hidden = (
    <input ref={input} type="file" multiple accept={ACCEPT} hidden data-testid="workspace-file-input"
      onChange={(e) => { take(Array.from(e.target.files ?? []), "picker"); e.target.value = "" }} />
  )

  if (variant === "compact") {
    return (
      <div className="wsd-compact">
        <button type="button" className="wsd-add" disabled={pending} onClick={() => input.current?.click()}>Add files</button>
        {hidden}
        {status}
      </div>
    )
  }

  return (
    <section className="wsd-hero" aria-labelledby="wsd-h">
      <h2 id="wsd-h" className="wsd-h">Start by adding the papers for this case</h2>
      <p className="wsd-lede">Add them once. Every session you start in this workspace can read them.</p>
      <div className={`wsd-zone${over ? " over" : ""}`} data-testid="matter-drop"
        onDragOver={(e) => { e.preventDefault(); setOver(true) }} onDragLeave={() => setOver(false)} onDrop={onDrop}>
        <p className="wsd-zone-t">{pending ? "Adding…" : "Drag files here"}</p>
        <p className="wsd-zone-s">or</p>
        <button type="button" className="ncf-btn primary" disabled={pending} onClick={() => input.current?.click()}>Choose files</button>
        <p className="wsd-formats">{DROP_FORMATS}</p>
        {hidden}
      </div>
      {status}
      <div className="wsd-eg">
        <p className="wsd-eg-h" id="wsd-eg-h">What to drop</p>
        <ul className="wsd-chips" aria-labelledby="wsd-eg-h">
          {DROP_EXAMPLES.map((e) => <li key={e}>{e}</li>)}
        </ul>
      </div>
    </section>
  )
})
